// Daily dispatch windows (optional) — battery operating constraint restricting
// WHEN the battery may charge or discharge within the day. Lives on the BESS
// page; the values are stored under arbitrageMerchant.regulatoryFramework
// (chargeWindow / dischargeWindow) because the backend optimizer reads them
// there, and they only apply to merchant/arbitrage runs.
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { DISPATCH_WINDOW_HELPER_TEXT } from "./config/regulatoryFramework";

const HOURS = Array.from({ length: 24 }, (_, h) => h);

function hourLabel(h) {
  return `${String(h).padStart(2, "0")}:00`;
}

function HourSelect({ label, value, onChange, disabled }) {
  return (
    <FormControl size="small" sx={{ minWidth: 110 }} disabled={disabled}>
      <InputLabel>{label}</InputLabel>
      <Select label={label} value={value ?? 0} onChange={(e) => onChange(Number(e.target.value))}>
        {HOURS.map((h) => (
          <MenuItem key={h} value={h}>
            {hourLabel(h)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export default function DispatchWindowsSection({ value, onChange }) {
  const rf = value || {};
  const patch = (partial) => onChange({ ...rf, ...partial });

  return (
    <Box
      sx={{
        mt: 2,
        p: 1.5,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.paper",
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
        Daily dispatch windows (optional)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {DISPATCH_WINDOW_HELPER_TEXT} Applies to Merchant / Trading (Arbitrage) runs.
      </Typography>
      {[
        { key: "chargeWindow", label: "Restrict charging hours" },
        { key: "dischargeWindow", label: "Restrict discharging hours" },
      ].map(({ key, label }) => {
        const w = rf[key] || { enabled: false, startHour: 0, endHour: 23 };
        return (
          <Box
            key={key}
            sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1, flexWrap: "wrap" }}
          >
            <FormControlLabel
              sx={{ minWidth: 230 }}
              control={
                <Checkbox
                  checked={Boolean(w.enabled)}
                  onChange={(e) => patch({ [key]: { ...w, enabled: e.target.checked } })}
                  size="small"
                />
              }
              label={label}
            />
            <HourSelect
              label="From"
              value={w.startHour}
              disabled={!w.enabled}
              onChange={(v) => patch({ [key]: { ...w, startHour: v } })}
            />
            <HourSelect
              label="To (incl.)"
              value={w.endHour}
              disabled={!w.enabled}
              onChange={(v) => patch({ [key]: { ...w, endHour: v } })}
            />
            <Typography variant="body2" color="text.secondary">
              {w.enabled
                ? `Allowed ${hourLabel(w.startHour)}–${hourLabel(w.endHour)}:59${
                    w.startHour > w.endHour ? " (wraps midnight)" : ""
                  }`
                : "Any time of day (current behaviour)"}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}
